import React, { useState } from 'react';
import {
  Col,
  Card,
  Row,
  Dropdown,
  Divider,
  Button,
  Popover,
  Alert,
  Space
} from 'antd';
import Paragraph from 'antd/lib/typography/Paragraph';
import { Template } from '../../../interfaces/backend/Template';
import { TemplatesPOSTRequest } from '../../../interfaces/backend/TemplatesPOSTRequest';
import SmsTemplateCard from './sms/SMSTemplateCard';
import EmailTemplateCard from './email/EmailTemplateCard';
import InAppTemplateCard from './inAppWeb/InAppTemplateCard';
import CallTemplateCard from './call/CallTemplateCard';
import { Channels } from '../../../interfaces/backend/Channels';
import { EmailerSendersGETResponse } from '../../../interfaces/backend/EmailerSendersGETResponse';
import TemplateDuplication from './TemplateDuplication';
import PushTemplateCard from './push/PushTemplateCard';
import WebPushTemplateCard from './webPush/WebPushTemplateCard';
import { InfoCircleOutlined, SettingOutlined } from '@ant-design/icons';
import TemplateCreationModal from './NewTemplateModal';
import { TemplatesPATCHDefaultRequest } from '../../../interfaces/backend/TemplatesPATCHDefaultRequest';
import {
  DeliveryOptions,
  Notification
} from '../../../interfaces/backend/Notifications';
import { EmailDeliveryOptionsEnum } from '../NotificationTabsContainer';
import { ActiveChannels } from '../../common/channelsEnum';

interface Props {
  deleteTemplate: (channel: string, templateId: string) => Promise<void>;
  makeDefault: (
    channel: string,
    templateId: string,
    modes?: TemplatesPATCHDefaultRequest['modes']
  ) => Promise<void>;
  patchTemplate: (
    channel: string,
    templateId: string,
    params: Record<string, unknown>
  ) => Promise<void>;
  createTemplate: (
    channel: string,
    params: TemplatesPOSTRequest
  ) => Promise<void>;
  templates?: Template[];
  notification: Notification;
  senders?: EmailerSendersGETResponse;
  activeChannels: Channels;
}

const TemplatesList = (props: Props): JSX.Element => {
  const [templateBeingDuplicated, setTemplateBeingDuplicated] =
    useState<Template>();
  const [showDisabledChannelsTemplates, setShowDisabledChannelsTemplates] =
    useState(false);
  const [showNewTemplateModal, setShowNewTemplateModal] = useState(false);

  const generateCardComponent = (template: Template) => {
    switch (template.channel) {
      case 'EMAIL':
        return (
          <EmailTemplateCard
            key={template.channel + template.templateId}
            deleteTemplate={props.deleteTemplate}
            makeDefault={props.makeDefault}
            patchTemplate={props.patchTemplate}
            createTemplate={props.createTemplate}
            template={template}
            notification={props.notification}
            sender={matchSenderWithAllSenders(
              template.senderEmail,
              props.senders ?? []
            )}
            setTemplateBeingDuplicated={setTemplateBeingDuplicated}
          />
        );
      case 'INAPP_WEB':
        return (
          <InAppTemplateCard
            key={template.channel + template.templateId}
            deleteTemplate={props.deleteTemplate}
            makeDefault={props.makeDefault}
            patchTemplate={props.patchTemplate}
            createTemplate={props.createTemplate}
            template={template}
            setTemplateBeingDuplicated={setTemplateBeingDuplicated}
            notification={props.notification}
          />
        );
      case 'SMS':
        return (
          <SmsTemplateCard
            key={template.channel + template.templateId}
            deleteTemplate={props.deleteTemplate}
            makeDefault={props.makeDefault}
            patchTemplate={props.patchTemplate}
            createTemplate={props.createTemplate}
            template={template}
            setTemplateBeingDuplicated={setTemplateBeingDuplicated}
          />
        );
      case 'CALL':
        return (
          <CallTemplateCard
            key={template.channel + template.templateId}
            deleteTemplate={props.deleteTemplate}
            makeDefault={props.makeDefault}
            patchTemplate={props.patchTemplate}
            createTemplate={props.createTemplate}
            template={template}
            setTemplateBeingDuplicated={setTemplateBeingDuplicated}
          />
        );

      case 'PUSH':
        return (
          <PushTemplateCard
            key={template.channel + template.templateId}
            deleteTemplate={props.deleteTemplate}
            makeDefault={props.makeDefault}
            patchTemplate={props.patchTemplate}
            createTemplate={props.createTemplate}
            template={template}
            setTemplateBeingDuplicated={setTemplateBeingDuplicated}
          />
        );

      case 'WEB_PUSH':
        return (
          <WebPushTemplateCard
            key={template.channel + template.templateId}
            deleteTemplate={props.deleteTemplate}
            makeDefault={props.makeDefault}
            patchTemplate={props.patchTemplate}
            createTemplate={props.createTemplate}
            template={template}
            setTemplateBeingDuplicated={setTemplateBeingDuplicated}
          />
        );
    }
  };

  return (
    <>
      <Row justify="space-between">
        <Col>
          <Paragraph type="secondary">
            The default templates used for this notification:
          </Paragraph>
        </Col>
        <Col>
          <Dropdown
            menu={{
              items: [
                {
                  key: '1',
                  label: `${
                    showDisabledChannelsTemplates ? 'Hide' : 'Show'
                  } disabled templates`,
                  onClick: () =>
                    setShowDisabledChannelsTemplates(
                      !showDisabledChannelsTemplates
                    )
                }
              ]
            }}
            trigger={['click']}
          >
            <SettingOutlined style={{ marginRight: '8px' }} />
          </Dropdown>
        </Col>
      </Row>
      {!props.templates && (
        <Col sm={24} md={12} lg={8}>
          <Card loading={true} />
        </Col>
      )}
      <Space direction="vertical" style={{ marginBottom: 20 }}>
        {[
          EmailDeliveryOptionsEnum.HOURLY,
          EmailDeliveryOptionsEnum.DAILY,
          EmailDeliveryOptionsEnum.WEEKLY,
          EmailDeliveryOptionsEnum.MONTHLY
        ].map((option) => {
          if (
            option !== EmailDeliveryOptionsEnum.OFF &&
            props.notification.channels.includes(ActiveChannels.EMAIL) &&
            (props.notification.options?.EMAIL as DeliveryOptions)[option]
              ?.enabled &&
            !props.templates?.find(
              (t) => t.isDefaultFor && t.isDefaultFor[option]
            )
          ) {
            return (
              <div key={option}>
                <Alert
                  type="warning"
                  description={`Click the "..." under an email template and choose it as the default ${option} template.`}
                  message={`No default email template for the ${option} batch.`}
                />
              </div>
            );
          } else {
            return null;
          }
        })}
      </Space>
      <Row gutter={[20, 20]}>
        {props.templates &&
          props.templates
            .filter((t) => t.default)
            .filter(
              (t) =>
                showDisabledChannelsTemplates ||
                props.activeChannels.includes(t.channel)
            )
            .map((template) => generateCardComponent(template))}
      </Row>

      <Divider orientation="left" orientationMargin={0}>
        Alternative Templates
        <Popover
          content={
            <div style={{ maxWidth: '400px' }}>
              <Paragraph type="secondary">
                You can create more templates to support different brandings,
                languages, and more.
              </Paragraph>
            </div>
          }
        >
          <InfoCircleOutlined style={{ marginLeft: '8px' }} />
        </Popover>
      </Divider>
      <Row>
        <Button onClick={() => setShowNewTemplateModal(true)}>
          Add Another Template
        </Button>
      </Row>

      <Row
        gutter={[20, 20]}
        style={{ marginTop: '20px', gridTemplateRows: 'masonry' }}
      >
        {props.templates &&
          props.templates
            .filter((t) => !t.default)
            .filter(
              (t) =>
                showDisabledChannelsTemplates ||
                props.activeChannels.includes(t.channel)
            )
            .map((template) => generateCardComponent(template))}
      </Row>

      <TemplateDuplication
        templateBeingDuplicated={templateBeingDuplicated}
        setTemplateBeingDuplicated={setTemplateBeingDuplicated}
        createTemplate={props.createTemplate}
      />

      <TemplateCreationModal
        open={showNewTemplateModal}
        setOpen={setShowNewTemplateModal}
        createTemplate={props.createTemplate}
      />
    </>
  );
};

export const matchSenderWithAllSenders = (
  senderEmail: string,
  allSenders: EmailerSendersGETResponse
): EmailerSendersGETResponse[number] | undefined => {
  const senderDomain = senderEmail.split('@')[1];

  // prioritize domains, I believe that's how SES works
  let sender = allSenders
    .filter((s) => s.type === 'domain')
    .find((s) => s.sender === senderDomain);
  if (sender) return sender;

  sender = allSenders
    .filter((s) => s.type === 'email')
    .find((s) => s.sender === senderEmail);

  return sender;
};

export default TemplatesList;
